import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Button from '../../../components/Button'
import Loader from '../../../components/Loader'
import ExamProgress from '../../../components/ExamProgress/ExamProgress'
import { PRIVATE_ROUTES } from '../../../Routes/routes'
import { getQuestionProgressByTalent } from '../../../services/Courses'
import { getAllTalentsFromAdmin } from '../../../services/Admin'
import { TAGS } from '../../../services/Tags'

// Seguimiento de exámenes de cursos por talento (master). Se busca al talento
// por nombre/usuario/email y se consulta su progreso de respuestas.
const ExamTracking = () => {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const [selectedTalent, setSelectedTalent] = useState(null)

  const { data: talents = [], isFetching: isSearching } = useQuery(
    ['exam-tracking-talents', submittedSearch],
    () => getAllTalentsFromAdmin({ textToSearch: submittedSearch }),
    {
      enabled: Boolean(submittedSearch),
      select: (data) => {
        const list = data?.talents
        return Array.isArray(list) ? list : []
      },
    }
  )

  const { data: progress = [], isFetching: isLoadingProgress } = useQuery(
    [TAGS.question_progress_by_talent, selectedTalent?.id],
    () => getQuestionProgressByTalent(selectedTalent.id),
    {
      enabled: Boolean(selectedTalent?.id),
      select: (data) => {
        const list = data?.res?.progress
        return Array.isArray(list) ? list : []
      },
    }
  )

  const handleSearch = (e) => {
    e.preventDefault()
    setSelectedTalent(null)
    setSubmittedSearch(searchText.trim())
  }

  const talentLabel = (talent) => {
    const user = talent?.user || {}
    const fullName = [user.names, user.surnames].filter(Boolean).join(' ')
    return fullName || user.username || user.email || `Talento #${talent?.id}`
  }

  return (
    <DashboardWrapper showTitle title="Seguimiento de exámenes">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-white border"
          onClick={() => navigate(PRIVATE_ROUTES.master_courses)}
        >
          ← Volver a cursos
        </button>
      </div>

      <p className="text-on-bg-secondary mb-4">
        Busca un talento para revisar las respuestas que ha dado en los
        exámenes de los cursos.
      </p>

      {/* Buscador de talentos */}
      <form
        onSubmit={handleSearch}
        className="d-flex gap-2 mb-4 flex-wrap"
        style={{ maxWidth: 560 }}
      >
        <input
          type="text"
          className="form-control"
          style={{ flex: 1, minWidth: 220 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Nombre, usuario o email del talento"
        />
        <Button type="submit" disabled={!searchText.trim()}>
          Buscar
        </Button>
      </form>

      {/* Resultados de la búsqueda */}
      {isSearching ? (
        <Loader />
      ) : (
        submittedSearch &&
        !selectedTalent && (
          <>
            {talents.length === 0 ? (
              <p className="text-on-bg-secondary mb-0">
                No se encontraron talentos para «{submittedSearch}».
              </p>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxWidth: 640 }}>
                {talents.map((talent) => (
                  <div
                    key={talent.id}
                    className="p-3 border rounded d-flex align-items-center gap-3 flex-wrap"
                  >
                    <div className="flex-grow-1" style={{ minWidth: 180 }}>
                      <p className="fw-bold text-on-bg-primary mb-0">
                        {talentLabel(talent)}
                      </p>
                      <p
                        className="text-on-bg-secondary mb-0"
                        style={{ fontSize: 13 }}
                      >
                        {talent?.user?.email}
                      </p>
                    </div>
                    <Button isSmall onClick={() => setSelectedTalent(talent)}>
                      Ver progreso
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}

      {/* Progreso del talento seleccionado */}
      {selectedTalent && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <h2 className="fw-bolder text-on-bg-primary mb-0" style={{ fontSize: 18 }}>
              Progreso de {talentLabel(selectedTalent)}
            </h2>
            <button
              type="button"
              className="btn btn-sm btn-white border"
              onClick={() => setSelectedTalent(null)}
            >
              ← Buscar otro talento
            </button>
          </div>

          {isLoadingProgress ? (
            <Loader />
          ) : (
            <ExamProgress
              progress={progress}
              emptyText="Este talento todavía no ha respondido exámenes."
            />
          )}
        </div>
      )}
    </DashboardWrapper>
  )
}

export default ExamTracking
